import type { OBRequestTask, OBTaskStatus } from "@/types/workflow"

export function formatDuration(ms: number): string {
  const minutes = Math.floor(ms / 60_000)
  if (minutes < 1) return "< 1 min"
  if (minutes < 60) return `${minutes} min`
  const hours = Math.floor(minutes / 60)
  const mins = minutes % 60
  if (hours < 24) return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`
  const days = Math.floor(hours / 24)
  const remainHours = hours % 24
  return remainHours > 0 ? `${days}d ${remainHours}h` : `${days}d`
}

/**
 * Compact version for narrow column cells — shows only the largest unit,
 * with a "+" suffix when sub-units are non-zero (e.g. "3d+", "2h+", "4m").
 */
export function formatDurationCompact(ms: number): string {
  const minutes = Math.floor(ms / 60_000)
  if (minutes < 1) return "< 1m"
  if (minutes < 60) return `${minutes}m`
  const hours = Math.floor(minutes / 60)
  const mins = minutes % 60
  if (hours < 24) return mins > 0 ? `${hours}h+` : `${hours}h`
  const days = Math.floor(hours / 24)
  const remainHours = hours % 24
  return remainHours > 0 ? `${days}d+` : `${days}d`
}

/**
 * Resolves the effective completedAt for a task:
 * - Uses task-level completedAt if present.
 * - Otherwise, if all actors are Completed, returns the most recent actor completedAt.
 * - Otherwise returns null (some actors still in progress / new).
 */
export function resolveTaskCompletedAt(task: OBRequestTask): string | null {
  if (task.completedAt) return task.completedAt

  const actors = task.actors ?? []
  if (actors.length === 0) return null

  const allDone = actors.every(a => a.status === "Completed")
  if (!allDone) return null

  const dates = actors
    .map(a => a.completedAt)
    .filter((d): d is string => !!d)
    .map(d => new Date(d).getTime())
    .filter(Number.isFinite)

  if (dates.length === 0) return null
  return new Date(Math.max(...dates)).toISOString()
}

export type TaskDurationVariant = "none" | "elapsed" | "fixed"

export interface TaskDurationResult {
  variant: TaskDurationVariant
  formatted: string | null
  ms: number | null
}

export function calcTaskDuration(task: OBRequestTask): TaskDurationResult {
  const status: OBTaskStatus = task.taskStatus

  if (status === "New" || !task.startDateTime) {
    return { variant: "none", formatted: null, ms: null }
  }

  const start = new Date(task.startDateTime).getTime()
  if (!Number.isFinite(start)) return { variant: "none", formatted: null, ms: null }

  const completedAt = resolveTaskCompletedAt(task)

  if (status === "Completed" && completedAt) {
    const end = new Date(completedAt).getTime()
    if (!Number.isFinite(end)) return { variant: "none", formatted: null, ms: null }
    const ms = Math.max(0, end - start)
    return { variant: "fixed", formatted: formatDuration(ms), ms }
  }

  // InProgress (or terminal statuses without a completedAt)
  const ms = Math.max(0, Date.now() - start)
  return { variant: "elapsed", formatted: formatDuration(ms), ms }
}

export interface SectionDurationResult {
  variant: "total" | "elapsed"
  formatted: string
}

export function calcSectionDuration(tasks: OBRequestTask[]): SectionDurationResult | null {
  if (tasks.length === 0) return null

  const starts = tasks
    .filter(t => t.startDateTime)
    .map(t => new Date(t.startDateTime!).getTime())
    .filter(Number.isFinite)

  if (starts.length === 0) return null
  const earliest = Math.min(...starts)

  const allCompleted = tasks.every(t => t.taskStatus === "Completed")

  if (allCompleted) {
    const completedDates = tasks
      .map(t => resolveTaskCompletedAt(t))
      .filter((d): d is string => !!d)
      .map(d => new Date(d).getTime())
      .filter(Number.isFinite)

    if (completedDates.length === 0) return null
    const latest = Math.max(...completedDates)
    const ms = Math.max(0, latest - earliest)
    return { variant: "total", formatted: formatDuration(ms) }
  }

  const ms = Math.max(0, Date.now() - earliest)
  return { variant: "elapsed", formatted: formatDuration(ms) }
}

export function formatPanelDate(iso: string | null | undefined): { date: string; time: string } | null {
  if (!iso) return null
  try {
    const d = new Date(iso)
    if (isNaN(d.getTime())) return null
    const date = d.toLocaleDateString("en-CA", { month: "short", day: "numeric", year: "numeric" })
    const time = d.toLocaleTimeString("en-CA", { hour: "numeric", minute: "2-digit", hour12: true }).toLowerCase()
    return { date, time }
  } catch {
    return null
  }
}
